import { useEffect, useState } from 'react';

/* ============================================================
   App preferences — localStorage-backed singleton with a tiny
   pub/sub so multiple components stay in sync.
   ------------------------------------------------------------
   Kept deliberately small: we only persist things the user can
   actually toggle today and that have real behavioural effect.
   Adding a preference here means wiring it through the relevant
   code path — no dead toggles.

   API:
     usePreferences()                — React hook with setters
     readPreference('hapticsEnabled') — sync read for non-React
                                        callers (e.g. haptics.ts)
   ============================================================ */

export interface Preferences {
  /** Whether iOS haptic feedback fires on capture / export / errors. */
  hapticsEnabled: boolean;
}

const DEFAULTS: Preferences = {
  hapticsEnabled: true,
};

const STORAGE_KEY = 'cadtomie-intraop:preferences';

function load(): Preferences {
  if (typeof window === 'undefined') return DEFAULTS;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULTS;
    const parsed = JSON.parse(raw) as Partial<Preferences>;
    return { ...DEFAULTS, ...parsed };
  } catch {
    return DEFAULTS;
  }
}

function save(prefs: Preferences): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    /* localStorage unavailable — silently skip */
  }
}

let cached: Preferences | null = null;
const subscribers = new Set<(p: Preferences) => void>();

function setPreference<K extends keyof Preferences>(key: K, value: Preferences[K]): void {
  if (!cached) cached = load();
  cached = { ...cached, [key]: value };
  save(cached);
  subscribers.forEach((cb) => cb(cached!));
}

/** Sync read — for code outside of React (the `haptic()` helper, etc.). */
export function readPreference<K extends keyof Preferences>(key: K): Preferences[K] {
  if (!cached) cached = load();
  return cached[key];
}

/** React hook with stable setters. All consumers re-render in lockstep
 *  when any preference is changed from anywhere in the app. */
export function usePreferences() {
  const [prefs, setPrefs] = useState<Preferences>(() => {
    if (!cached) cached = load();
    return cached;
  });

  useEffect(() => {
    subscribers.add(setPrefs);
    return () => {
      subscribers.delete(setPrefs);
    };
  }, []);

  return {
    ...prefs,
    setHapticsEnabled: (v: boolean) => setPreference('hapticsEnabled', v),
  };
}
